import React, { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

import { useCredentialByTypeQuery } from '../../../redux/external-agency';
import LoadingSpinner from '../../../components/spinner/LoadingSpinner';
import checkIcon from '../../../assets/images/check.svg';
import redCloseIcon from '../../../assets/images/close-red.svg';

const AgencyProfessionalsCredentialData = ({ caregiverId, rowData }) => {
	const [rows, setRows] = useState({});
	const { data: credentialData, isFetching } = useCredentialByTypeQuery({
		caregiverId,
		credType: rowData.credential_type,
		source: 'table'
	});

	const processStatus = (status) => {
		if (!status) {
			return 'pending';
		}
		if (status.startsWith('is_')) {
			return status.replace('is_', '').replaceAll('_', ' ');
		}
		return status.replaceAll('_', ' ').toLowerCase();
	};

	const mapCredentialStatus = (credential) => ({
		...credential,
		data: credential.data.map((row) => ({
			...row,
			status: processStatus(row.status)
		}))
	});

	const loadingTemplate = () => {
		return <LoadingSpinner className="saving-spinner" />;
	};

	const defaultTemplate = (item, columnData) => {
		const { field } = columnData;

		if (typeof item[field] === 'boolean') {
			return <>{item[field] ? <img src={checkIcon} alt="success" /> : <img src={redCloseIcon} alt="failed" />}</>;
		}

		return <span>{item[field] ? item[field] : '-'}</span>;
	};

	const getListTemplate = (credentialCode) => (item, columnData) => {
		const { field } = columnData;
		const list = item[field];

		if (list.length <= 1) {
			return <span>{list[0]?.name ?? '-'}</span>;
		}

		return (
			<ul style={{ marginBottom: 0, paddingLeft: '16px' }}>
				{item[field].map((listItem, idx) => (
					// eslint-disable-next-line react/no-array-index-key
					<li key={`list-item-${credentialCode}-${item.id}-${field}-${idx}`}>{listItem.name}</li>
				))}
			</ul>
		);
	};

	useEffect(() => {
		if (credentialData && rowData.credential_type) {
			setRows((prev) => ({
				...prev,
				[rowData.credential_type]: mapCredentialStatus(credentialData)
			}));
		}
	}, [credentialData, rowData.credential_type]);

	const columns = () => {
		if (isFetching) {
			return <Column key="loader" field="loading" body={loadingTemplate} />;
		}
		return rows[rowData.credential_type]?.columns?.map((column) => {
			if (column.renderer && column.renderer === 'edit') {
				return (
					<Column
						key="actions"
						field="actions"
						header="Actions"
						style={{ width: '2%', padding: '0.8rem 1.6rem' }}
						alignFrozen="right"
					/>
				);
			}

			if (column?.renderer === 'list_renderer') {
				return (
					<Column
						key={column?.data}
						field={column?.data}
						sortable
						header={column?.title}
						body={(item, columnData) => getListTemplate(rowData.credential_type)(item, columnData)}
					/>
				);
			}

			return (
				<Column
					key={column?.data}
					field={column?.data}
					sortable
					header={column?.title}
					style={{ padding: '1.6rem 1.6rem' }}
					className={column?.data === 'status' ? 'status-column' : ''}
					body={defaultTemplate}
				/>
			);
		});
	};

	return (
		<DataTable
			loading={isFetching || !Object.keys(rows).length}
			value={rows[rowData.credential_type]?.data}
			className="credential-data"
			tableStyle={{ width: '150%' }}
			removableSort
			loadingTemplate={loadingTemplate}
		>
			{columns()}
		</DataTable>
	);
};

export default AgencyProfessionalsCredentialData;
